import React, {useCallback, useEffect, useState} from "react";
import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    SimpleGrid,
    Text,
    useColorModeValue,
    useToast
} from "@chakra-ui/react";
import {MdAdd, MdChevronLeft, MdChevronRight, MdFilterList, MdRefresh, MdSearch, MdVpnKey} from "react-icons/md";
import ColumnsTablePermission from "./components/ColumnsTablePermission";
import {columnsDataPermission} from "./variables/columnsDataPermission";
import Card from "components/card/Card";
import axios from "axios";

export default function Settings({onDataUpdate}) {
    const [tableData, setTableData] = useState([]);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize] = useState(15);
    const [totalPages, setTotalPages] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [filters, setFilters] = useState({serviceName: "", menuName: "", subMenuName: ""});
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newData, setNewData] = useState({service: "", menu: "", subMenu: "", action: "", description: ""});
    const toast = useToast();

    const inputBgColor = useColorModeValue("white", "gray.700");
    const inputBorderColor = useColorModeValue("gray.200", "gray.600");
    const headingColor = useColorModeValue("navy.700", "white");
    const mutedTextColor = useColorModeValue("secondaryGray.600", "secondaryGray.500");
    const filterBgColor = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
    const iconBgColor = useColorModeValue("brand.100", "whiteAlpha.100");
    const modalBg = useColorModeValue("white", "navy.800");

    const fetchPermissions = useCallback(async () => {
        setIsLoading(true);
        setLoadError("");

        try {
            const token = document.cookie.split('; ').find(row => row.startsWith('token=')).split('=')[1];
            const response = await fetch(
                process.env.REACT_APP_API_URL + `/v1/permission?page=${pageIndex + 1}&size=${pageSize}&serviceName=${filters.serviceName}&menuName=${filters.menuName}&subMenuName=${filters.subMenuName}`,
                {headers: {Authorization: `Bearer ${token}`}}
            );
            const data = await response.json();

            if (!data.success) {
                throw new Error("Failed to fetch users data");
            }

            setTableData(data.data.content);
            setTotalPages(data.data.totalPages);
        } catch (error) {
            console.error("Error fetching data:", error);
            setLoadError(error.message || "Unable to load permission data.");
        } finally {
            setIsLoading(false);
        }
    }, [filters.menuName, filters.serviceName, filters.subMenuName, pageIndex, pageSize]);

    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions]);

    const handleDataUpdate = () => {
        fetchPermissions();
    };

    const handleFilterChange = (e) => {
        setFilters({...filters, [e.target.name]: e.target.value});
        setPageIndex(0);
    };

    const resetFilters = () => {
        setFilters({serviceName: "", menuName: "", subMenuName: ""});
        setPageIndex(0);
    };

    const handleAddDataChange = (e) => {
        setNewData({...newData, [e.target.name]: e.target.value});
    };

    const handleAddData = async () => {
        const token = document.cookie.split('; ').find(row => row.startsWith('token=')).split('=')[1];
        try {
            const response = await axios.post(process.env.REACT_APP_API_URL + '/v1/permission', newData, {
                headers: {Authorization: `Bearer ${token}`}
            });

            if (response.status === 200) {
                toast({
                    title: 'Permission created.',
                    description: 'You data successfully saved.',
                    status: 'success',
                    duration: 5000,
                    isClosable: true
                });
                setNewData({nrp: ''});
                setIsAddModalOpen(false);
                handleDataUpdate();
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: 'There was an issue creating your account.',
                status: 'error',
                duration: 5000,
                isClosable: true
            });
        }
    };

    const hasActiveFilters = Object.values(filters).some(Boolean);

    return (
        <Box pt={{base: "130px", md: "80px", xl: "80px"}}>
            <Card mb="24px" p={{base: "20px", md: "24px"}}>
                <Flex direction={{base: "column", md: "row"}} justify="space-between" align={{base: "flex-start", md: "center"}} gap={5}>
                    <Flex align="center" gap={4}>
                        <Flex w="48px" h="48px" borderRadius="16px" bg={iconBgColor} align="center" justify="center">
                            <Icon as={MdVpnKey} color="brand.500" boxSize={6}/>
                        </Flex>
                        <Box>
                            <Heading as="h1" size="lg" color={headingColor}>Master Permission</Heading>
                            <Text color={mutedTextColor} mt={1} fontSize="sm">Kelola izin akses service, menu, dan action dalam satu workspace.</Text>
                        </Box>
                    </Flex>
                    <Flex wrap="wrap" gap={3}>
                        <Button variant="light" size="sm" leftIcon={<Icon as={MdRefresh}/>} onClick={fetchPermissions} isLoading={isLoading}>Refresh data</Button>
                        <Button colorScheme="brand" size="sm" leftIcon={<Icon as={MdAdd}/>} onClick={() => setIsAddModalOpen(true)}>Tambah data</Button>
                    </Flex>
                </Flex>
            </Card>

            <Card mb="24px" p={{base: "20px", md: "24px"}}>
                <Flex justify="space-between" align={{base: "flex-start", sm: "center"}} direction={{base: "column", sm: "row"}} gap={3} mb={5}>
                    <Flex align="center" gap={2}>
                        <Icon as={MdFilterList} color="brand.500" boxSize={5}/>
                        <Box>
                            <Heading as="h2" size="sm" color={headingColor}>Filter permission</Heading>
                            <Text color={mutedTextColor} fontSize="sm">Persempit daftar berdasarkan service, menu, atau sub menu.</Text>
                        </Box>
                    </Flex>
                    <Button variant="ghost" size="sm" onClick={resetFilters} isDisabled={!hasActiveFilters}>Reset filter</Button>
                </Flex>
                <SimpleGrid columns={{base: 1, md: 3}} spacing={4} bg={filterBgColor} p={{base: 3, md: 4}} borderRadius="16px">
                    <FormControl>
                        <FormLabel htmlFor="permission-service-filter" fontSize="sm" color={headingColor}>Service</FormLabel>
                        <InputGroup><InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="secondaryGray.500"/></InputLeftElement><Input id="permission-service-filter" placeholder="Cari service" name="serviceName" value={filters.serviceName} onChange={handleFilterChange} bg={inputBgColor} borderColor={inputBorderColor}/></InputGroup>
                    </FormControl>
                    <FormControl>
                        <FormLabel htmlFor="permission-menu-filter" fontSize="sm" color={headingColor}>Menu</FormLabel>
                        <InputGroup><InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="secondaryGray.500"/></InputLeftElement><Input id="permission-menu-filter" placeholder="Cari menu" name="menuName" value={filters.menuName} onChange={handleFilterChange} bg={inputBgColor} borderColor={inputBorderColor}/></InputGroup>
                    </FormControl>
                    <FormControl>
                        <FormLabel htmlFor="permission-submenu-filter" fontSize="sm" color={headingColor}>Sub menu</FormLabel>
                        <InputGroup><InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="secondaryGray.500"/></InputLeftElement><Input id="permission-submenu-filter" placeholder="Cari sub menu" name="subMenuName" value={filters.subMenuName} onChange={handleFilterChange} bg={inputBgColor} borderColor={inputBorderColor}/></InputGroup>
                    </FormControl>
                </SimpleGrid>
            </Card>

            {loadError ? (
                <Card p={{base: "20px", md: "24px"}}>
                    <Alert status="error" borderRadius="12px" alignItems="flex-start"><AlertIcon mt={1}/><Box><Text fontWeight="700">Data permission tidak dapat dimuat</Text><Text fontSize="sm" mt={1}>{loadError}</Text><Button mt={4} size="sm" colorScheme="red" onClick={fetchPermissions} leftIcon={<Icon as={MdRefresh}/>}>Coba lagi</Button></Box></Alert>
                </Card>
            ) : (
                <>
                    <ColumnsTablePermission columnsData={columnsDataPermission} tableData={tableData} onDataUpdate={handleDataUpdate} isLoading={isLoading}/>
                    <Card p={{base: "16px", md: "20px"}}>
                        <Flex direction={{base: "column", sm: "row"}} justify="space-between" align="center" gap={4}>
                            <Button variant="light" size="sm" leftIcon={<Icon as={MdChevronLeft}/>} onClick={() => setPageIndex((prev) => Math.max(prev - 1, 0))} isDisabled={pageIndex === 0 || isLoading}>Sebelumnya</Button>
                            <Text color={mutedTextColor} fontSize="sm" textAlign="center">Halaman <Text as="span" color={headingColor} fontWeight="700">{pageIndex + 1}</Text> dari <Text as="span" color={headingColor} fontWeight="700">{totalPages}</Text></Text>
                            <Button variant="light" size="sm" rightIcon={<Icon as={MdChevronRight}/>} onClick={() => setPageIndex((prev) => Math.min(prev + 1, totalPages - 1))} isDisabled={pageIndex + 1 === totalPages || isLoading}>Berikutnya</Button>
                        </Flex>
                    </Card>
                </>
            )}

            <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} isCentered>
                <ModalOverlay/>
                <ModalContent bg={modalBg} borderRadius="20px">
                    <ModalHeader borderBottomWidth="1px">Add Data Permission</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody py={6}>
                        <FormControl mb={4} isInvalid={!newData.service}><FormLabel>Name Service</FormLabel><Input name="service" value={newData.service} onChange={handleAddDataChange} bg={inputBgColor}/></FormControl>
                        <FormControl mb={4} isInvalid={!newData.menu}><FormLabel>Menu</FormLabel><Input name="menu" value={newData.menu} onChange={handleAddDataChange} bg={inputBgColor}/></FormControl>
                        <FormControl mb={4} isInvalid={!newData.subMenu}><FormLabel>subMenu</FormLabel><Input name="subMenu" value={newData.subMenu} onChange={handleAddDataChange} bg={inputBgColor}/></FormControl>
                        <FormControl mb={4} isInvalid={!newData.action}><FormLabel>Action</FormLabel><Select name="action" value={newData.action} onChange={handleAddDataChange} bg={inputBgColor}><option value="">Select Action</option><option value="view">View</option><option value="add">Add</option><option value="edit">Edit</option><option value="delete">Delete</option></Select></FormControl>
                        <FormControl isInvalid={!newData.description}><FormLabel>Description</FormLabel><Input name="description" value={newData.description} onChange={handleAddDataChange} bg={inputBgColor}/></FormControl>
                    </ModalBody>
                    <ModalFooter borderTopWidth="1px">
                        <Button variant="ghost" mr={3} onClick={() => { setIsAddModalOpen(false); setNewData({service: "", menu: "", subMenu: "", action: "", description: ""}); }}>Cancel</Button>
                        <Button colorScheme="blue" onClick={() => {
                            setIsAddModalOpen(false);
                            let Swal = require('sweetalert2');
                            Swal.fire({
                                title: 'Are you sure?',
                                text: "Please confirm to save this data",
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#3085d6',
                                cancelButtonColor: '#d33',
                                confirmButtonText: 'Yes, save it!'
                            }).then((result) => {
                                if (result.isConfirmed) {
                                    handleAddData();
                                } else {
                                    setIsAddModalOpen(true);
                                }
                            });
                        }} isDisabled={!newData.service || !newData.menu || !newData.subMenu || !newData.action || !newData.description}>Add Data</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
